import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Store, Plus, RefreshCw, Loader2, Search, X,
  CheckCircle, AlertCircle, Clock, XCircle,
  Building2, Mail, Phone, Hash,
  Pencil, UserPlus, AlertTriangle, Trash2,
  Plug, ChevronRight, ChevronDown, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import AmazonConnectModal from '../../components/Admin/SellerAccounts/AmazonConnectModal';
import FlipkartConnectModal from '../../components/Admin/SellerAccounts/FlipkartConnectModal';
import MeeshoConnectModal from '../../components/Admin/SellerAccounts/MeeshoConnectModal';
import { sellerService } from '../../services/sellerService';
import { sellerAccountService } from '../../services/sellerAccountService';

// ─── Platform SVG Icons ─────────────────────────────────────────────────────

function AmazonIcon({ size = 18 }) {
  return <img src="/assets/marketplaces/amazon-icon.png" alt="Amazon" width={size} height={size} className="object-contain" />;
}

function FlipkartIcon({ size = 18 }) {
  return <img src="/assets/marketplaces/flipkart-icon.png" alt="Flipkart" width={size} height={size} className="object-contain" />;
}

function MeeshoIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#6D2077"/>
      <path d="M7 8.5C7 7.672 7.672 7 8.5 7s1.5.672 1.5 1.5V16H7V8.5zM11 10.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5V16h-3v-5.5zM15 8.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5V16h-3V8.5z" fill="white"/>
    </svg>
  );
}

const PlatformIcon = ({ platform, size = 18 }) => {
  switch (platform) {
    case 'AMAZON': return <AmazonIcon size={size} />;
    case 'FLIPKART': return <FlipkartIcon size={size} />;
    case 'MEESHO': return <MeeshoIcon size={size} />;
    default: return null;
  }
};

// ─── Config ─────────────────────────────────────────────────────────────────

const platformConfig = {
  AMAZON: { label: 'Amazon', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
  FLIPKART: { label: 'Flipkart', bg: 'bg-[#FFF4E6]', border: 'border-[#FFD89E]', text: 'text-[#C45500]' },
  MEESHO: { label: 'Meesho', bg: 'bg-[#F7F7F7]', border: 'border-[#D5D9D9]', text: 'text-[#37475A]' },
};

const sellerStatusConfig = {
  ACTIVE: { label: 'Active', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  INACTIVE: { label: 'Inactive', color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400' },
  SUSPENDED: { label: 'Suspended', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
};

// Channel & Notification filter options
const CHANNEL_OPTIONS = [
  { value: 'AMAZON', label: 'Amazon' },
  { value: 'FLIPKART', label: 'Flipkart' },
  { value: 'MEESHO', label: 'Meesho' },
];

const NOTIFICATION_OPTIONS = [
  { value: 'DAILY_ORDER_SALES', label: 'Daily Sales' },
  { value: 'REVIEW_REQUEST', label: 'Review Request' },
  { value: 'PRODUCT_LISTING_STATUS', label: 'Listing Status' },
  { value: 'FEEDBACK_REVIEW', label: 'Feedback Review' },
  { value: 'PRODUCT_HIJACK', label: 'Hijack Alert' },
];

// ─── Confirm Modal ──────────────────────────────────────────────────────────

function ConfirmModal({ isOpen, onClose, onConfirm, title, message, variant = 'warning', loading }) {
  if (!isOpen) return null;
  const colors = variant === 'danger'
    ? { icon: 'bg-red-100 text-red-600', btn: 'bg-red-600 hover:bg-red-700' }
    : { icon: 'bg-amber-100 text-amber-600', btn: 'bg-amber-600 hover:bg-amber-700' };
  const Icon = variant === 'danger' ? Trash2 : AlertTriangle;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="p-6">
          <div className={`w-12 h-12 rounded-xl ${colors.icon} flex items-center justify-center mb-4`}><Icon size={22} /></div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">{title}</h3>
          <p className="text-sm text-slate-500">{message}</p>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${colors.btn} disabled:opacity-50 flex items-center gap-2`}>
            {loading && <Loader2 size={14} className="animate-spin" />}Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Add/Edit Seller Modal ──────────────────────────────────────────────────

function AddSellerModal({ isOpen, onClose, onSave, editData }) {
  const [form, setForm] = useState({ businessName: '', legalName: '', gstin: '', pan: '', contactEmail: '', contactPhone: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editData) setForm({ businessName: editData.businessName || '', legalName: editData.legalName || '', gstin: editData.gstin || '', pan: editData.pan || '', contactEmail: editData.contactEmail || '', contactPhone: editData.contactPhone || '', address: editData.address || '', notes: editData.notes || '' });
    else setForm({ businessName: '', legalName: '', gstin: '', pan: '', contactEmail: '', contactPhone: '', address: '', notes: '' });
  }, [editData, isOpen]);
  if (!isOpen) return null;
  const handleSubmit = async (e) => { e.preventDefault(); if (!form.businessName.trim()) return; setSaving(true); try { await onSave(form); } finally { setSaving(false); } };
  const isEdit = !!editData;
  const inputCls = "w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent";
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-[#FFF9EE] to-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#232F3E] flex items-center justify-center">
              {isEdit ? <Pencil size={18} className="text-white" /> : <UserPlus size={18} className="text-white" />}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{isEdit ? 'Edit Seller' : 'Add New Seller'}</h3>
              <p className="text-xs text-gray-500">{isEdit ? 'Update business information' : 'Onboard a new e-commerce seller'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Business Name <span className="text-red-500">*</span></label><input type="text" value={form.businessName} onChange={(e) => setForm(f => ({ ...f, businessName: e.target.value }))} placeholder="e.g., Kaka Light & Sons" required className={inputCls} /></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Legal Name</label><input type="text" value={form.legalName} onChange={(e) => setForm(f => ({ ...f, legalName: e.target.value }))} placeholder="Registered legal name" className={inputCls} /></div>
            <div><label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">GSTIN</label><input type="text" value={form.gstin} onChange={(e) => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="22AAAAA0000A1Z5" maxLength={15} className={`${inputCls} font-mono`} /></div>
            <div><label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">PAN</label><input type="text" value={form.pan} onChange={(e) => setForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))} placeholder="ABCDE1234F" maxLength={10} className={`${inputCls} font-mono`} /></div>
            <div><label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Email</label><input type="email" value={form.contactEmail} onChange={(e) => setForm(f => ({ ...f, contactEmail: e.target.value }))} placeholder="seller@example.com" className={inputCls} /></div>
            <div><label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Phone</label><input type="tel" value={form.contactPhone} onChange={(e) => setForm(f => ({ ...f, contactPhone: e.target.value }))} placeholder="+91 98765 43210" className={inputCls} /></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Address</label><textarea value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Business address" rows={2} className={`${inputCls} resize-none`} /></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Notes</label><textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Internal notes" rows={2} className={`${inputCls} resize-none`} /></div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={saving || !form.businessName.trim()} className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-[#232F3E] hover:bg-[#37475A] rounded-lg transition-colors disabled:opacity-50">
              {saving && <Loader2 size={16} className="mr-2 animate-spin" />}{saving ? 'Saving...' : (isEdit ? 'Update Seller' : 'Add Seller')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// ─── Platform Picker ────────────────────────────────────────────────────────

function ConnectPlatformPicker({ isOpen, onClose, onSelect }) {
  if (!isOpen) return null;
  const platforms = [
    { key: 'AMAZON', label: 'Amazon SP-API', desc: 'Connect via Seller Central' },
    { key: 'FLIPKART', label: 'Flipkart', desc: 'Connect via Seller Dashboard' },
    { key: 'MEESHO', label: 'Meesho', desc: 'Connect via Supplier Panel' },
  ];
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Choose Platform</h3><p className="text-xs text-slate-500 mt-0.5">Select a marketplace to connect</p></div>
        <div className="p-3 space-y-2">
          {platforms.map(p => {
            const pc = platformConfig[p.key];
            return (
              <button key={p.key} onClick={() => onSelect(p.key)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-left`}>
                <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 shadow-sm"><PlatformIcon platform={p.key} size={20} /></div>
                <div><span className="text-sm font-medium text-slate-800 block">{p.label}</span><span className="text-[11px] text-slate-400">{p.desc}</span></div>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Multi-Select Dropdown (for Channels & Notification filters) ────────────

function MultiSelectDropdown({ label, options, selected, onChange, width = 170 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (value) => {
    if (selected.includes(value)) onChange(selected.filter(v => v !== value));
    else onChange([...selected, value]);
  };

  const displayText = selected.length === 0
    ? label
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || label
      : `${label} (${selected.length})`;

  return (
    <div className="relative" ref={ref} style={{ width }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] ${
          selected.length > 0
            ? 'border-[#FFD89E] bg-[#FFF4E6]/40 text-[#C45500] font-medium'
            : 'border-gray-300 bg-white text-gray-700'
        }`}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown size={14} className={`ml-2 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${selected.length > 0 ? 'text-[#f08804]' : 'text-gray-400'}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {options.map(opt => {
            const isSelected = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-[#FFF4E6] transition-colors text-left"
              >
                <span className={`w-4 h-4 flex items-center justify-center rounded border flex-shrink-0 ${
                  isSelected ? 'bg-[#232F3E] border-[#C45500]' : 'border-gray-300 bg-white'
                }`}>
                  {isSelected && <Check size={11} className="text-white" />}
                </span>
                <span className="flex-1 truncate">{opt.label}</span>
              </button>
            );
          })}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full px-3 py-2 text-xs text-red-500 hover:bg-red-50 border-t border-gray-100 text-left"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Filter Bar (compact, white theme, live-apply) ──────────────────────────

const emptyFilters = {
  businessName: '',
  pan: '',
  gstin: '',
  contactPhone: '',
  contactEmail: '',
  status: '',
  channels: [],           // multi-select
  notificationConfig: [], // multi-select
};

function FilterBar({ filters, onChange, onReset, resultCount, totalCount }) {
  const hasActiveFilters =
    Object.entries(filters).some(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== ''));
  const inputCls = "px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]";

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={filters.businessName}
          onChange={e => onChange('businessName', e.target.value)}
          placeholder="Business name"
          className={`${inputCls} w-[180px]`}
        />
        <input
          type="text"
          value={filters.pan}
          onChange={e => onChange('pan', e.target.value.toUpperCase())}
          placeholder="PAN"
          maxLength={10}
          className={`${inputCls} font-mono w-[140px]`}
        />
        <input
          type="text"
          value={filters.gstin}
          onChange={e => onChange('gstin', e.target.value.toUpperCase())}
          placeholder="GSTIN"
          maxLength={15}
          className={`${inputCls} font-mono w-[180px]`}
        />
        <input
          type="text"
          value={filters.contactPhone}
          onChange={e => onChange('contactPhone', e.target.value)}
          placeholder="Phone"
          className={`${inputCls} w-[150px]`}
        />
        <input
          type="email"
          value={filters.contactEmail}
          onChange={e => onChange('contactEmail', e.target.value)}
          placeholder="Email"
          className={`${inputCls} w-[180px]`}
        />
        <select
          value={filters.status}
          onChange={e => onChange('status', e.target.value)}
          className={inputCls}
        >
          <option value="">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="SUSPENDED">Suspended</option>
        </select>

        {/* ── New: Channels multi-select ── */}
        <MultiSelectDropdown
          label="Channels"
          options={CHANNEL_OPTIONS}
          selected={filters.channels}
          onChange={vals => onChange('channels', vals)}
          width={160}
        />

        {/* ── New: Notification Config multi-select ── */}
        <MultiSelectDropdown
          label="Notification Config"
          options={NOTIFICATION_OPTIONS}
          selected={filters.notificationConfig}
          onChange={vals => onChange('notificationConfig', vals)}
          width={190}
        />

        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800"
          >
            <X size={14} className="mr-1" /> Clear
          </button>
        )}

        <span className="ml-auto text-sm text-gray-500">
          {resultCount} of {totalCount} sellers
        </span>
      </div>
    </div>
  );
}

// ─── Seller Row ─────────────────────────────────────────────────────────────

function SellerRow({ seller, onNavigate, onEdit, onDelete, onConnectChannel }) {
  const channels = seller.channels || [];
  const ss = sellerStatusConfig[seller.status] || sellerStatusConfig.ACTIVE;
  const connectedCount = channels.filter(c => c.accountStatus === 'AUTHORIZED').length;
  const uniquePlatforms = [...new Set(channels.map(c => c.platform))];

  return (
    <div className="bg-white rounded-xl border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all cursor-pointer group" onClick={onNavigate}>
      <div className="flex items-center gap-5 px-6 py-5">
        {/* Seller Icon */}
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#f08804] to-[#e88b00] flex items-center justify-center flex-shrink-0 shadow-sm shadow-[#FFD89E]">
          <Building2 size={20} className="text-white" />
        </div>

        {/* Seller Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 mb-0.5">
            <h3 className="text-sm font-bold text-slate-900 truncate group-hover:text-[#C45500] transition-colors">{seller.businessName}</h3>
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
              <span className={`text-[10px] font-semibold ${ss.color}`}>{ss.label}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {seller.gstin && <span className="text-[11px] text-slate-400 flex items-center gap-1 font-mono"><Hash size={9} className="text-slate-300" />{seller.gstin}</span>}
            {seller.contactEmail && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Mail size={9} className="text-slate-300" />{seller.contactEmail}</span>}
            {seller.contactPhone && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Phone size={9} className="text-slate-300" />{seller.contactPhone}</span>}
          </div>
        </div>

        {/* Connected Platforms - always visible */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {channels.length > 0 ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                {uniquePlatforms.map(p => {
                  const pc = platformConfig[p];
                  const channelsForPlatform = channels.filter(c => c.platform === p);
                  const connectedForPlatform = channelsForPlatform.filter(c => c.accountStatus === 'AUTHORIZED').length;
                  const allConnected = connectedForPlatform === channelsForPlatform.length;
                  return (
                    <div key={p} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${allConnected ? pc.border : 'border-slate-200'} ${allConnected ? pc.bg : 'bg-slate-50'} transition-colors`} title={`${pc.label}: ${connectedForPlatform}/${channelsForPlatform.length} connected`}>
                      <PlatformIcon platform={p} size={16} />
                      {channelsForPlatform.length > 1 && <span className={`text-[10px] font-bold ${allConnected ? pc.text : 'text-slate-400'}`}>{channelsForPlatform.length}</span>}
                      {allConnected ? <CheckCircle size={10} className="text-emerald-500" /> : <AlertCircle size={10} className="text-amber-500" />}
                    </div>
                  );
                })}
              </div>
              <div className="h-5 w-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium whitespace-nowrap">{connectedCount}/{channels.length}</span>
            </div>
          ) : (
            <span className="text-xs text-slate-300 font-medium flex items-center gap-1.5"><Plug size={12} /> No channels</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button onClick={onEdit} title="Edit seller" className="p-2 rounded-lg text-slate-400 hover:text-[#C45500] hover:bg-[#FFF4E6] transition-colors"><Pencil size={14} /></button>
          <button onClick={() => onConnectChannel(seller)} title="Connect marketplace" className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"><Plus size={14} /></button>
          {channels.length === 0 && <button onClick={() => onDelete(seller)} title="Delete seller" className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>}
        </div>

        {/* Navigate Arrow */}
        <ChevronRight size={16} className="text-slate-300 group-hover:text-[#f08804] transition-colors flex-shrink-0" />
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function SellerAccountsPage() {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Filter state (live-applied, no separate applied state) ──
  const [filters, setFilters] = useState(emptyFilters);

  const [addSellerModal, setAddSellerModal] = useState({ open: false, editData: null });
  const [platformPicker, setPlatformPicker] = useState({ open: false, sellerId: null });
  const [amazonModal, setAmazonModal] = useState({ open: false, sellerId: null });
  const [flipkartModal, setFlipkartModal] = useState({ open: false, sellerId: null });
  const [meeshoModal, setMeeshoModal] = useState({ open: false, sellerId: null });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', variant: 'warning', action: null });
  const [confirmLoading, setConfirmLoading] = useState(false);

  const loadSellers = useCallback(async () => {
    try {
      const res = await sellerService.getAll();
      const data = res.data || res;
      setSellers(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load sellers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSellers(); }, [loadSellers]);

  const handleSaveSeller = async (formData) => {
    try {
      if (addSellerModal.editData) { await sellerService.update(addSellerModal.editData.id, formData); toast.success('Seller updated'); }
      else { await sellerService.create(formData); toast.success('Seller added'); }
      setAddSellerModal({ open: false, editData: null });
      loadSellers();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to save seller'); }
  };

  const handleDeleteSeller = (seller) => {
    setConfirmModal({ open: true, variant: 'danger', title: 'Delete Seller', message: `Permanently delete "${seller.businessName}"? This cannot be undone.`, action: async () => { await sellerService.delete(seller.id); toast.success('Seller deleted'); loadSellers(); } });
  };

  const handleConnectChannel = (seller) => { setPlatformPicker({ open: true, sellerId: seller.id }); };

  const handlePlatformSelect = (platform) => {
    const sid = platformPicker.sellerId;
    setPlatformPicker({ open: false, sellerId: null });
    if (platform === 'AMAZON') setAmazonModal({ open: true, sellerId: sid });
    else if (platform === 'FLIPKART') setFlipkartModal({ open: true, sellerId: sid });
    else if (platform === 'MEESHO') setMeeshoModal({ open: true, sellerId: sid });
  };

  const handleConnect = async (data) => {
    if (data?._oauthComplete) {
      setAmazonModal({ open: false, sellerId: null });
      loadSellers();
      return;
    }
    try {
      await sellerAccountService.connect(data);
      toast.success('Channel connected');
      setAmazonModal({ open: false, sellerId: null }); setFlipkartModal({ open: false, sellerId: null }); setMeeshoModal({ open: false, sellerId: null });
      loadSellers();
    } catch (err) { toast.error(err?.response?.data?.message || 'Connection failed'); }
  };

  const handleConfirm = async () => {
    if (!confirmModal.action) return;
    setConfirmLoading(true);
    try { await confirmModal.action(); } catch (err) { toast.error(err?.response?.data?.message || 'Action failed'); }
    finally { setConfirmLoading(false); setConfirmModal({ open: false, title: '', message: '', variant: 'warning', action: null }); }
  };

  // ── Filter handlers (live-apply) ──
  const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }));
  const handleResetFilters = () => setFilters(emptyFilters);

  // ── Combined filter + search logic (uses filters directly — live apply) ──
  const filteredSellers = sellers.filter(s => {
    // search query (existing)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesSearch = s.businessName?.toLowerCase().includes(q) || s.gstin?.toLowerCase().includes(q) || s.contactEmail?.toLowerCase().includes(q) || s.channels?.some(c => c.sellerName?.toLowerCase().includes(q) || c.platform?.toLowerCase().includes(q));
      if (!matchesSearch) return false;
    }
    // panel filters — applied live
    const f = filters;
    if (f.businessName && !s.businessName?.toLowerCase().includes(f.businessName.toLowerCase())) return false;
    if (f.pan && !s.pan?.toLowerCase().includes(f.pan.toLowerCase())) return false;
    if (f.gstin && !s.gstin?.toLowerCase().includes(f.gstin.toLowerCase())) return false;
    if (f.contactPhone && !s.contactPhone?.includes(f.contactPhone)) return false;
    if (f.contactEmail && !s.contactEmail?.toLowerCase().includes(f.contactEmail.toLowerCase())) return false;
    if (f.status && s.status !== f.status) return false;

    // Channels filter (multi-select, OR logic): seller must have at least one channel matching any selected platform
    if (f.channels && f.channels.length > 0) {
      const sellerPlatforms = (s.channels || []).map(c => c.platform);
      const hasAny = f.channels.some(cp => sellerPlatforms.includes(cp));
      if (!hasAny) return false;
    }

    // Notification Config filter (multi-select, OR logic)
    // Supports either s.notificationConfig (array of strings) or s.notificationConfigs
    if (f.notificationConfig && f.notificationConfig.length > 0) {
      const configs = s.notificationConfig || s.notificationConfigs || s.notifications || [];
      const configValues = Array.isArray(configs)
        ? configs.map(c => (typeof c === 'string' ? c : (c.type || c.channel || c.name || '')).toUpperCase())
        : [];
      const hasAny = f.notificationConfig.some(nc => configValues.includes(nc));
      if (!hasAny) return false;
    }

    return true;
  });

  const totalChannels = sellers.reduce((sum, s) => sum + (s.channels?.length || 0), 0);
  const connectedChannels = sellers.reduce((sum, s) => sum + (s.channels?.filter(c => c.accountStatus === 'AUTHORIZED').length || 0), 0);

  return (
    <AdminLayout>
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Sellers & Channels</h1>
            <p className="text-sm text-slate-500 mt-0.5">{sellers.length} seller{sellers.length !== 1 ? 's' : ''} &middot; {connectedChannels}/{totalChannels} channels connected</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadSellers} className="p-2.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" title="Refresh"><RefreshCw size={16} /></button>
            <button onClick={() => setAddSellerModal({ open: true, editData: null })} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] text-[#0F1111] rounded-lg hover:from-[#f0c14b] hover:to-[#e7ae00] text-sm font-semibold transition-all shadow-sm"><Plus size={16} /> Add Seller</button>
          </div>
        </div>

        {/* ── Filter Bar (compact, white, live-apply) ── */}
        {sellers.length > 0 && (
          <FilterBar
            filters={filters}
            onChange={handleFilterChange}
            onReset={handleResetFilters}
            resultCount={filteredSellers.length}
            totalCount={sellers.length}
          />
        )}

        {/* ── Search ── */}
{/*         {sellers.length > 0 && ( */}
{/*           <div className="relative max-w-md"> */}
{/*             <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /> */}
{/*             <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sellers, channels, GSTIN..." className="w-full pl-9 pr-8 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent" /> */}
{/*             {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"><X size={14} className="text-slate-400" /></button>} */}
{/*           </div> */}
{/*         )} */}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center"><Loader2 size={32} className="mx-auto text-[#f08804] animate-spin mb-3" /><p className="text-sm text-slate-500">Loading sellers...</p></div>
          </div>
        ) : sellers.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 rounded-2xl bg-[#FFE8C8] flex items-center justify-center mx-auto mb-4"><Store size={28} className="text-[#f08804]" /></div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No sellers yet</h3>
              <p className="text-sm text-slate-500 mb-4">Start by adding a seller, then connect their marketplace accounts.</p>
              <button onClick={() => setAddSellerModal({ open: true, editData: null })} className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] text-[#0F1111] rounded-lg hover:from-[#f0c14b] hover:to-[#e7ae00] text-sm font-semibold transition-all shadow-sm"><Plus size={16} /> Add Your First Seller</button>
            </div>
          </div>
        ) : filteredSellers.length === 0 ? (
          <div className="text-center py-16"><p className="text-sm text-slate-500">No results for current filters.</p></div>
        ) : (
          <div className="space-y-3">
            {filteredSellers.map(seller => (
              <SellerRow key={seller.id} seller={seller}
                onNavigate={() => navigate(`/sellers/${seller.id}`)}
                onEdit={() => setAddSellerModal({ open: true, editData: seller })}
                onDelete={handleDeleteSeller}
                onConnectChannel={handleConnectChannel}
              />
            ))}
          </div>
        )}
      </div>

      <AddSellerModal isOpen={addSellerModal.open} onClose={() => setAddSellerModal({ open: false, editData: null })} onSave={handleSaveSeller} editData={addSellerModal.editData} />
      <ConnectPlatformPicker isOpen={platformPicker.open} onClose={() => setPlatformPicker({ open: false, sellerId: null })} onSelect={handlePlatformSelect} />
      <AmazonConnectModal isOpen={amazonModal.open} onClose={() => setAmazonModal({ open: false, sellerId: null })} onConnect={handleConnect} sellerId={amazonModal.sellerId} />
      <FlipkartConnectModal isOpen={flipkartModal.open} onClose={() => setFlipkartModal({ open: false, sellerId: null })} onConnect={handleConnect} sellerId={flipkartModal.sellerId} />
      <MeeshoConnectModal isOpen={meeshoModal.open} onClose={() => setMeeshoModal({ open: false, sellerId: null })} onConnect={handleConnect} sellerId={meeshoModal.sellerId} />
      <ConfirmModal isOpen={confirmModal.open} onClose={() => setConfirmModal({ open: false, title: '', message: '', variant: 'warning', action: null })} onConfirm={handleConfirm} title={confirmModal.title} message={confirmModal.message} variant={confirmModal.variant} loading={confirmLoading} />
    </AdminLayout>
  );
}